import React, { useRef, useState, useCallback } from 'react'
import { faChevronDown, faCopy, faPen, faPrint, faSpinnerThird, faTimes, faTrash, faAngleRight as nextIcon } from '@fortawesome/pro-regular-svg-icons'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material'
import Button from 'src/components/era-package/forms/Button'
import Buttons from 'src/components/era-package/era/Buttons'
import SnackBar from 'src/components/era-package/era/SnackBar'
import { useHistory } from 'react-router-dom/cjs/react-router-dom'
import pathMap from 'src/paths/pathMap'
import { generatePath } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import EditEntrySummary from './entry-form/edit/EditEntrySummary'
import AbiEntrySummaryApiUrls from "src/apiUrls/AbiEntrySummaryApiUrls"
import { FormType, PrintDialog } from './PrintDialog'

const formTypes = Object.keys(FormType).reduce((acc, key) => {
  acc[key] = key
  return acc
}, {})

export const DetailModal = ({
  onClose, id, fetchData, rowData, ...props
}) => {
  const dialogRef = useRef(null)
  const history = useHistory()

  const [loading, setLoading] = useState(false)
  const [formsMenuOpen, setFormsMenuOpen] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [openCloneDialog, setOpenCloneDialog] = useState(false)
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [printDialogType, setPrintDialogType] = useState(null)
  const cloneEntryLabel = rowData?.entryNumber || id


  const handleClose = (...args) => {
    if (typeof onClose === 'function')
      onClose(...args)
  }

  const handleEdit = () => {
    handleClose()
    history.push(generatePath(pathMap.ABI_ENTRY_SUMMARY_EDIT_VIEW, { id }))
  }

  const handleCloneDialog = () => {
    setOpenCloneDialog(true)
  }

  const handleClone = () => {

    setLoading(true)

    // BACKEND TODO: clone the item with provided `id` and generate a new ID
    fetch(AbiEntrySummaryApiUrls.CLONE + `?abiTransId=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      },
    }).then(res => res.json())
      .then(json => {
        setLoading(false)
        if (!!json.data) {
          setOpenCloneDialog(false)
          handleClose()
          history.push(generatePath(pathMap.ABI_ENTRY_SUMMARY_EDIT_VIEW, { id: json.data.abiTransId }))
        }
      })
      .catch(err => {
        setLoading(false)
        console.error(err)
      })
  }

  const handleDeleteDialog = () => {
    setOpenDeleteDialog(true)
  }

  const handleDelete = () => {
    setLoading(true)

    fetch(AbiEntrySummaryApiUrls.DELETE + `?abiTransId=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      },
    }).then(res => res.json())
      .then(json => {
        setLoading(false)
        if (!!json.data) {
          setOpenDeleteDialog(false)
          handleClose()
          fetchData()
        }
      })
      .catch(err => {
        setLoading(false)
        console.error(err)
      })
  }

  // Handle keyboard navigation for accessibility
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClose()
    } else if (event.key === 'Tab') {
      // Ensure focus stays within the modal
      const dialog = dialogRef.current
      if (dialog) {
        const focusableElements = dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault()
            lastElement.focus()
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault()
            firstElement.focus()
          }
        }
      }
    }
  }

  const handleOpenPrintDialog = (formType) => {
    console.log('clicked', formType)
    setFormsMenuOpen(null)
    setPrintDialogType(formType)
  }

  return (
    <>
      {/* Error Snackbar - Only show when showErrorSnackbar is true */}
      <SnackBar
        title="Error"
        type="error"
        open={showErrorSnackbar}
        onClose={() => {
          setShowErrorSnackbar(false)
          setErrorMessage('')
        }}
        duration={8000}
      >
        {errorMessage || "Internal error occurred, please try again later."}
      </SnackBar>

      <Dialog
        onClose={handleClose}
        ref={dialogRef}
        PaperProps={{
          sx: {
            maxWidth: '100dvw',
            width: '100dvw',
            maxHeight: {
              xs: '100dvh',
              sm: '90vh',
            },
            m: {
              xs: 0,
              sm: 4,
            },
          },
        }}
        TransitionProps={{
          onEntered: () => {
            // Comprehensive cleanup of any remaining focused elements in main wrapper
            const mainWrapper = document.querySelector('.mainwrapper')
            if (mainWrapper) {
              // Find and blur any focused elements
              const focusedElements = mainWrapper.querySelectorAll(':focus')
              focusedElements.forEach((element) => {
                element.blur()
              })

              // Extra safety: blur any MUI buttons that might be retaining focus
              const muiButtons = mainWrapper.querySelectorAll(
                '.MuiButton-root, .MuiButtonBase-root',
              )
              muiButtons.forEach((button) => {
                if (document.activeElement === button) {
                  button.blur()
                }
              })
            }

            // Ensure focus is properly set in the modal
            setTimeout(() => {
              const dialog = dialogRef.current
              if (dialog) {
                const firstFocusable = dialog.querySelector(
                  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
                )
                if (firstFocusable) {
                  firstFocusable.focus()
                }
              }
            }, 10)
          },
        }}
        disableEnforceFocus={false}
        disableAutoFocus={false}
        disableRestoreFocus={false}
        onKeyDown={handleKeyDown}
        aria-labelledby="view-entry-summary-modal-title"
        aria-describedby="view-entry-summary-modal-description"
        role="dialog"
        {...props}
      >
        <DialogTitle
          id="view-entry-summary-modal-title"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          View Entry Summary
          <IconButton
            aria-label="Close view entry summary modal"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
            title="Close modal"
          >
            <Box component={FontAwesomeIcon} icon={faTimes} sx={{ width: '1em' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          id="view-entry-summary-modal-description"
          sx={{
            px: {
              xs: 2,
              sm: 4,
            },
          }}
        >
          <Box>
            <EditEntrySummary isEntrySummary readOnly id={id} />
          </Box>
        </DialogContent>

        <DialogActions sx={{ pt: 3 }}>
          <Buttons
            advance={<>
              <Button disabled={loading}
                onClick={handleCloneDialog} title="Clone a copy of this Entry Summary"
                startIcon={<FontAwesomeIcon icon={loading ? faSpinnerThird : faCopy} spin={loading} />}>
                Clone
              </Button>
              <Button variant="contained" onClick={handleEdit}
                title="Edit this Entry Summary" disabled={loading}
                startIcon={<FontAwesomeIcon icon={faPen} />}
              >
                Edit
              </Button>
              <Button onClick={handleClose} variant="text" title="Close modal"
              >
                Done
              </Button>
            </>

            }
            other={
              <>
                {rowData != null && rowData["3461Status"] == null && rowData["7501Status"] == null && (
                  <Button disabled={loading}
                    variant="outlined"
                    onClick={handleDeleteDialog}
                    color="error"
                    title="Proceed with Caution: Delete this Entry Summary"
                    startIcon={<FontAwesomeIcon icon={faTrash} />}
                  >
                    Delete
                  </Button>
                )}
                <Button disabled={loading}
                  variant="outlined"
                  startIcon={<FontAwesomeIcon icon={faPrint} />}
                  endIcon={<FontAwesomeIcon icon={faChevronDown} rotation={!!formsMenuOpen ? 180 : 0} />}
                  onClick={(e) => setFormsMenuOpen(e.currentTarget)}
                >
                  Generate Forms
                </Button>
                <Menu anchorEl={formsMenuOpen} open={Boolean(formsMenuOpen)} onClose={() => setFormsMenuOpen(null)}>
                  <MenuItem onClick={() => handleOpenPrintDialog(formTypes.FORM_3461)}>
                    Form 3461
                  </MenuItem>
                  <MenuItem onClick={() => handleOpenPrintDialog(formTypes.FORM_7501)}>
                    Form 7501
                  </MenuItem>
                </Menu>
              </>
            }
          />
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Are you sure you want to delete this record?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete}
            color="error"
            disabled={loading}
            endIcon={<FontAwesomeIcon icon={loading ? faSpinnerThird : nextIcon} width={10} spin={loading} />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCloneDialog} onClose={() => setOpenCloneDialog(false)}>
        <DialogTitle>{`Clone Entry Summary: ${cloneEntryLabel}?`}</DialogTitle>
        <DialogContent>
          {`You are requesting to clone Entry Summary Number: ${cloneEntryLabel}`}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCloneDialog(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleClone}
            variant="contained"
            disabled={loading}
            startIcon={<FontAwesomeIcon icon={loading ? faSpinnerThird : faCopy} spin={loading} />}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <PrintDialog onClose={() => setPrintDialogType(null)}
        transId={id} formType={printDialogType}
      />
    </>
  )
}